/**
 * Mark Uploaded Images Plugin for CKEditor 5
 * 
 * This plugin marks images that are uploaded (via paste, drag & drop, or upload button)
 * with a special class 'cke-uploaded-image'. This allows us to identify and remove
 * these images when sending to social media platforms that handle images as separate attachments.
 */

import { Plugin } from 'ckeditor5';

export default class MarkUploadedImages extends Plugin {
	static get pluginName() {
		return 'MarkUploadedImages';
	}

	static get requires() {
		return ['ImageUpload'];
	}

	init() {
		const editor = this.editor;
		
		// Use the conversion system to add the class during upcast (HTML -> Model)
		// and downcast (Model -> HTML)
		this._setupConversion();

		// Track uploaded images and mark them
		const fileRepository = editor.plugins.get('FileRepository');
		
		fileRepository.on('change:uploaded', (evt, name, value) => {
			if (value === true) {
				// Mark the most recently inserted image
				setTimeout(() => this._markRecentImage(), 50);
			}
		});
	}

	_setupConversion() {
		const editor = this.editor;
		
		// Add class to images during downcast (when converting model to view/HTML)
		editor.conversion.for('downcast').add(dispatcher => {
			dispatcher.on('attribute:src:imageBlock', (evt, data, conversionApi) => {
				const viewWriter = conversionApi.writer;
				const figure = conversionApi.mapper.toViewElement(data.item);
				const img = figure.getChild(0);
				
				if (img && img.is('element', 'img')) {
					const src = data.attributeNewValue;
					// Mark uploaded images (not base64 or archive)
					if (src && !src.startsWith('data:') && (src.includes('/errand/') || src.includes('/web/file/'))) {
						viewWriter.addClass('cke-uploaded-image', img);
					}
				}
			}, { priority: 'low' });

			dispatcher.on('attribute:src:imageInline', (evt, data, conversionApi) => {
				const viewWriter = conversionApi.writer;
				const img = conversionApi.mapper.toViewElement(data.item);
				
				if (img && img.is('element', 'img')) {
					const src = data.attributeNewValue;
					// Mark uploaded images (not base64 or archive)
					if (src && !src.startsWith('data:') && (src.includes('/errand/') || src.includes('/web/file/'))) {
						viewWriter.addClass('cke-uploaded-image', img);
					}
				}
			}, { priority: 'low' });
		});
	}

	_markRecentImage() {
		const editor = this.editor;
		const model = editor.model;

		model.change(writer => {
			const root = model.document.getRoot();
			const range = model.createRangeIn(root);

			// Find the last inserted image
			let lastImage = null;
			for (const value of range.getWalker({ ignoreElementEnd: true })) {
				if (value.item.is('element', 'imageBlock') || value.item.is('element', 'imageInline')) {
					const src = value.item.getAttribute('src');
					if (src && !src.startsWith('data:')) {
						lastImage = value.item;
					}
				}
			}

			// Mark it in the model (this will trigger the downcast conversion)
			if (lastImage) {
				// The class will be added during downcast
				// We just need to trigger a re-render
				const src = lastImage.getAttribute('src');
				writer.setAttribute('src', src, lastImage);
			}
		});
	}
}

